import React, { useEffect, useState } from "react";
import SideBar from "./SideBar";
import axiosClient from "../axios";
import { Dialog, CircularProgress } from "@mui/material";
import { ReactComponent as Picadd } from "../assets/images/newAddPic.svg";

const AppImages = () => {
  // Image states
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [imageName, setImageName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Video states
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoName, setVideoName] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoSourceUrl, setVideoSourceUrl] = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoFileKey, setVideoFileKey] = useState(Date.now());
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagesData, setPagesData] = useState({}); // { page: { videos, nextToken } }
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [videosError, setVideosError] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchPage = async (page, token) => {
    setLoadingVideos(true);
    setVideosError("");
    try {
      const res = await axiosClient.get("/getAllVideosWithHLS", {
        params: { limit: rowsPerPage, token: token || null }
      });
      if (res.data.status) {
        setPagesData(prev => ({
          ...prev,
          [page]: {
            videos: res.data.data,
            nextToken: res.data.nextToken || null
          }
        }));
        return { success: true, nextToken: res.data.nextToken };
      } else {
        setVideosError("Failed to load videos.");
        return { success: false };
      }
    } catch (err) {
      console.error("Error fetching videos:", err);
      setVideosError("Error loading videos. Please try again.");
      return { success: false };
    } finally {
      setLoadingVideos(false);
      setInitialLoading(false);
    }
  };

  const resetAndFetchFirstPage = async () => {
    setCurrentPage(1);
    setPagesData({});
    setInitialLoading(true);
    await fetchPage(1, null);
  };

  const handleRowsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setRowsPerPage(newLimit);
    resetAndFetchFirstPage();
  };

  const handleNextPage = async () => {
    const currentPageData = pagesData[currentPage];
    if (!currentPageData || !currentPageData.nextToken) return;

    const nextPage = currentPage + 1;
    if (pagesData[nextPage]) {
      setCurrentPage(nextPage);
    } else {
      await fetchPage(nextPage, currentPageData.nextToken);
      setCurrentPage(nextPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    resetAndFetchFirstPage();
  }, [rowsPerPage]);

  const refreshVideoList = () => {
    resetAndFetchFirstPage();
  };

  const currentVideos = pagesData[currentPage]?.videos || [];
  const hasNextPage = pagesData[currentPage]?.nextToken !== null && pagesData[currentPage]?.nextToken !== undefined;
  const hasPrevPage = currentPage > 1;

  // Close image dialog and reset form
  const handleCloseImageDialog = () => {
    setOpen(false);
    setImageName("");
    setSelectedImageFile(null);
    setImageUploading(false);
    setFileInputKey(Date.now());
  };

  // Close video dialog and reset form
  const handleCloseVideoDialog = () => {
    setVideoOpen(false);
    setVideoName("");
    setSelectedVideoFile(null);
    setVideoUploading(false);
    setVideoFileKey(Date.now());
  };

  // Validate and store image file (no upload)
  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileSizeInMb = file.size / (1024 * 1024);
    if (fileSizeInMb > 6) {
      alert("File size is too large. Please choose an image smaller than 6 MB.");
      e.target.value = "";
      return;
    }

    setSelectedImageFile(file);
  };

  // Upload image on button click
  const handleImageUpload = async () => {
    if (!imageName.trim()) {
      alert("Please enter an image name.");
      return;
    }
    if (!selectedImageFile) {
      alert("Please select an image file.");
      return;
    }

    setImageUploading(true);
    const formData = new FormData();
    formData.append("photo", selectedImageFile);

    try {
      const res = await axiosClient.post(
        `/addImageWithCustomName?image_name=${encodeURIComponent(imageName)}`,
        formData
      );
      setImageUrl(res.data.response.url);
      alert("Image uploaded successfully!");
      handleCloseImageDialog();
    } catch (err) {
      console.error("Image upload error:", err);
      alert("Image upload failed. Please try again.");
    } finally {
      setImageUploading(false);
    }
  };

  // Validate and store video file (no upload)
  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileSizeInMb = file.size / (1024 * 1024);
    if (fileSizeInMb > 100) {
      alert("File size is too large. Please choose a video smaller than 100 MB.");
      e.target.value = "";
      return;
    }

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'mp4') {
      alert("Only MP4 files are allowed.");
      e.target.value = "";
      return;
    }

    setSelectedVideoFile(file);
  };

  // Upload video on button click
  const handleVideoUpload = async () => {
    if (!videoName.trim()) {
      alert("Please enter a video name.");
      return;
    }
    if (!selectedVideoFile) {
      alert("Please select an MP4 video file.");
      return;
    }

    setVideoUploading(true);
    const formData = new FormData();
    formData.append("video", selectedVideoFile);

    try {
      const res = await axiosClient.post(
        `/addVideoWithHLS?video_name=${encodeURIComponent(videoName)}`,
        formData
      );
      if (res.data.status) {
        setVideoUrl(res.data.response.hls_url);
        setVideoSourceUrl(res.data.response.source_url);
        alert("Video uploaded! HLS will be ready in ~1-2 minutes.");
        handleCloseVideoDialog();
        refreshVideoList(); 
      } else {
        alert("Video upload failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Video upload error:", err);
      alert("Video upload failed. Please try again.");
    } finally {
      setVideoUploading(false);
    }
  };

  const copyToClipboard = (text, label = "URL") => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied!`);
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <>
      <SideBar handleSideBar={true} activeTab={"AppImages"} />
      <div className="admin-screen-v4">
        {/* Header with buttons */}
        <div className="admin-header-v4">
          <h2>App Images & Videos</h2>
          <div className="button-group-v4">
            <button className="btn-primary-v4" onClick={() => setOpen(true)}>
              Add Image
            </button>
            <button
              className="btn-primary-v4"
              style={{ background: "#6c5ce7", backgroundImage: "none" }}
              onClick={() => setVideoOpen(true)}
            >
              Add Video
            </button>
          </div>
        </div>

        {/* Display uploaded Image Link */}
        {imageUrl && (
          <div className="image-link-container" style={{ marginBottom: "20px" }}>
            <span>Image Link</span>
            <input type="text" value={imageUrl} readOnly className="form-input-v4" />
            <button onClick={() => copyToClipboard(imageUrl, "Image link")} className="btn-action-table-v4">
              Copy
            </button>
          </div>
        )}

        {/* Display uploaded Video Links */}
        {videoUrl && (
          <div style={{ padding: "15px 20px", background: "var(--bg-input)", borderRadius: "var(--radius-md)", marginBottom: "20px" }}>
            <div className="image-link-container" style={{ marginBottom: "10px" }}>
              <span>HLS URL</span>
              <input type="text" value={videoUrl} readOnly className="form-input-v4" />
              <button onClick={() => copyToClipboard(videoUrl, "HLS URL")} className="btn-action-table-v4">
                Copy
              </button>
            </div>
            <div className="image-link-container">
              <span>Source MP4 URL</span>
              <input type="text" value={videoSourceUrl} readOnly className="form-input-v4" />
              <button onClick={() => copyToClipboard(videoSourceUrl, "Source URL")} className="btn-action-table-v4">
                Copy
              </button>
            </div>
            <p style={{ color: "#e17055", fontSize: "13px", marginTop: "8px" }}>
              ⚠ HLS URL will work after ~1-2 minutes (transcoding in progress)
            </p>
          </div>
        )}

        {/* All Videos Table Section */}
        <div style={{ marginTop: "30px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3 className="section-header-v4">All Uploaded Videos</h3>
          </div>

          {initialLoading && (
            <div className="loader-container-v4">
              <CircularProgress />
            </div>
          )}
          {videosError && <p className="text-muted-v4" style={{ color: "var(--danger-color)" }}>{videosError}</p>}
          {!initialLoading && !videosError && currentVideos.length === 0 && (
            <div className="empty-state-v4">
              <div className="empty-icon-v4">🎬</div>
              <h3>No videos uploaded yet</h3>
              <p>Upload your first video using the "Add Video" button.</p>
            </div>
          )}
          {!initialLoading && !videosError && currentVideos.length > 0 && (
            <>
              {/* Next / Previous Pagination */}
              <div className="pagination-v4">
                <div className="pagination-info-v4">
                  <div className="pagination-info-v4">
                    <span>Rows per page:</span>
                    <select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="pagination-select-v4"
                      style={{ marginLeft: "8px" }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <span>Page {currentPage}</span>
                </div>
                <div className="pagination-buttons-v4">
                  {hasPrevPage && <button
                    onClick={handlePrevPage}
                    disabled={!hasPrevPage || loadingVideos}
                    className="btn-secondary-v4"
                  >
                    Previous
                  </button>}
                  {hasNextPage &&
                    <button
                      onClick={handleNextPage}
                      disabled={!hasNextPage || loadingVideos}
                      className="btn-secondary-v4"
                    >
                      Next
                    </button>
                  }
                </div>
              </div>
              <div className="table-container-v4">
                <table className="data-table-v4">
                  <thead>
                    <tr>
                      <th>Video Name</th>
                      <th>Source URL (MP4)</th>
                      <th>HLS URL (Main)</th>
                      <th>Size</th>
                      <th>Last Modified</th>
                      <th>Copy HLS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVideos.map((video, idx) => (
                      <tr key={idx}>
                        <td className="text-mono-v4">{video.video_name}</td>
                        <td className="text-link-v4">
                          <a href={video.source_url} target="_blank" rel="noopener noreferrer">
                            {video.source_url.substring(0, 60)}...
                          </a>
                        </td>
                        <td className="text-link-v4">
                          <a href={video.hls.main} target="_blank" rel="noopener noreferrer">
                            {video.hls.main.substring(0, 60)}...
                          </a>
                        </td>
                        <td>{formatFileSize(video.size)}</td>
                        <td>{new Date(video.last_modified).toLocaleString()}</td>
                        <td>
                          <button
                            onClick={() => copyToClipboard(video.hls.main, "HLS URL")}
                            className="btn-action-table-v4"
                          >
                            Copy HLS
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Image Upload Dialog */}
      <Dialog open={open} onClose={handleCloseImageDialog} className="dialogs">
        <div className="modal-container-v4" style={{ maxWidth: "500px", margin: "auto" }}>
          <div className="modal-header-v4">
            <h3 className="modal-title-v4">Add Image</h3>
            <button className="close-btn-v4" onClick={handleCloseImageDialog}>×</button>
          </div>
          <div className="modal-body-v4">
            <div className="form-group-v4">
              <label className="form-label-v4">Image Name <span className="required-star-v4">*</span></label>
              <input
                type="text"
                value={imageName}
                onChange={(e) => setImageName(e.target.value)}
                placeholder="Enter Image Name"
                className="form-input-v4"
              />
            </div>
            <div className="form-group-v4">
              <label className="form-label-v4">Select Image (max 6MB)</label>
              <label htmlFor="inputTagField" className="upload-label-v4">
                <Picadd className="upload-icon-v4" />
                <span className="upload-text-v4">Click to upload</span>
                <input
                  type="file"
                  id="inputTagField"
                  key={fileInputKey}
                  accept="image/*"
                  onChange={handleImageFileSelect}
                  className="file-input-v4"
                />
              </label>
              {selectedImageFile && (
                <p style={{ fontSize: "12px", color: "var(--success-color)", marginTop: "5px" }}>
                  ✓ {selectedImageFile.name}
                </p>
              )}
            </div>
            {imageUploading && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px" }}>
                <CircularProgress size={24} />
                <span>Uploading image...</span>
              </div>
            )}
          </div>
          <div className="modal-footer-v4">
            <button onClick={handleCloseImageDialog} className="cancel-button-v4">Cancel</button>
            <button
              onClick={handleImageUpload}
              disabled={imageUploading}
              className="submit-button-v4"
              style={{ opacity: imageUploading ? 0.6 : 1 }}
            >
              {imageUploading ? "Uploading..." : "Get Image Link"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Video Upload Dialog */}
      <Dialog open={videoOpen} onClose={handleCloseVideoDialog} className="dialogs">
        <div className="modal-container-v4" style={{ maxWidth: "500px", margin: "auto" }}>
          <div className="modal-header-v4" style={{ background: "#6c5ce7" }}>
            <h3 className="modal-title-v4">Add Video</h3>
            <button className="close-btn-v4" onClick={handleCloseVideoDialog}>×</button>
          </div>
          <div className="modal-body-v4">
            <div className="form-group-v4">
              <label className="form-label-v4">Video Name <span className="required-star-v4">*</span></label>
              <input
                type="text"
                value={videoName}
                onChange={(e) => setVideoName(e.target.value)}
                placeholder="Enter Video Name"
                className="form-input-v4"
              />
            </div>
            <div className="form-group-v4">
              <label className="form-label-v4">Select Video (MP4 only, max 100MB)</label>
              <label htmlFor="videoInputField" className="upload-label-v4">
                <Picadd className="upload-icon-v4" />
                <span className="upload-text-v4">Click to upload</span>
                <input
                  type="file"
                  id="videoInputField"
                  key={videoFileKey}
                  accept="video/mp4"
                  onChange={handleVideoFileSelect}
                  className="file-input-v4"
                />
              </label>
              {selectedVideoFile && (
                <p style={{ fontSize: "12px", color: "var(--success-color)", marginTop: "5px" }}>
                  ✓ {selectedVideoFile.name} ({(selectedVideoFile.size / (1024 * 1024)).toFixed(2)} MB)
                </p>
              )}
            </div>
            {videoUploading && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px" }}>
                <CircularProgress size={24} />
                <span>Uploading video...</span>
              </div>
            )}
          </div>
          <div className="modal-footer-v4">
            <button onClick={handleCloseVideoDialog} className="cancel-button-v4">Cancel</button>
            <button
              onClick={handleVideoUpload}
              disabled={videoUploading}
              className="submit-button-v4"
              style={{ background: "#6c5ce7", opacity: videoUploading ? 0.6 : 1 }}
            >
              {videoUploading ? "Uploading..." : "Get Video Link"}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AppImages;